/**
 * # 2FA verification API
 *
 * ## Endpoint: `${backendHost}${backendLoginVerifyUrl}` (`/sessions/verify`)
 *
 * Used for verifying the 2FA code (legacy email OTP and the new authenticator/TOTP flow).
 *
 * ### Response Cases
 *
 * 1. **Status Code: 401 (Unauthorized), Empty JSON Body**
 *    - Description: A failed verification attempt (invalid code).
 *
 * 2. **Status Code: 200 (OK), Response Body: `{ redirect_to: 'some_url' }`**
 *    - Description: Successful verification. The `redirect_to` property contains the URL to redirect to.
 *
 * 3. **Status Code: 401 (Unauthorized), Response Body: `{ remaining_attempts: 3 }`** (legacy email OTP)
 *    - Description: A failed verification attempt with the number of remaining attempts.
 *
 * ## Endpoint: `${backendHost}${backendLoginVerifyBackupUrl}` (`/sessions/verify_backup`)
 *
 * Used to sign in with a single-use backup code when the authenticator app is unavailable.
 */

/* eslint-disable @typescript-eslint/naming-convention */
import { fetchWithCsrf } from 'utils/fetchWithCsrf';
import * as Sentry from '@sentry/react';
import {
    backendHost,
    apiHeaders,
    backendLoginVerifyUrl,
    backendLoginVerifyBackupUrl,
} from 'utils/constants';
import { reportUserDataMissing } from 'utils/adobeAnalytics/utils/utils';
import { trackFormInteraction } from 'utils/adobeAnalytics/trackFormInteraction';

// Backend marker returned (alongside a redirect_to) on the 401 when the single-use 2FA verification
// session has been consumed/expired. Distinct from a plain wrong-code 401, which has an empty body.
const SESSION_EXPIRED_ERROR = 'Verification session expired';

type VerifyResponse = {
    redirect_to?: string;
    error?: string;
    user_id?: string;
    company_id?: string;
    company_name?: string;
    remaining_attempts?: number;
};

type VerifyBackupResponse = {
    redirect_to?: string;
    error?: string;
    user_id?: string;
    company_id?: string;
    company_name?: string;
    backup_code_used?: boolean;
    backup_codes_remaining?: number;
};

export const verifyCode = async (data: { code: string; return_to?: string | null }) => {
    try {
        const response = await fetchWithCsrf(`${backendHost}${backendLoginVerifyUrl}`, {
            method: 'POST',
            mode: backendHost?.length ? 'cors' : 'same-origin',
            headers: apiHeaders,
            body: JSON.stringify(data),
        });
        const json: VerifyResponse = await response.json();
        const { redirect_to, remaining_attempts, user_id, company_id, company_name } = json;

        if (redirect_to) {
            if (!!user_id && !!company_id && !!company_name) {
                trackFormInteraction({
                    formName: 'signin-otp-form',
                    interactionType: 'submit',
                    account: { companyID: company_id, companyName: company_name },
                    user: { id: user_id },
                });
            } else {
                reportUserDataMissing({ user_id, company_id, company_name });
            }

            return { redirectTo: redirect_to };
        }

        if (remaining_attempts !== undefined) {
            return remaining_attempts === 0 ? { error: true } : { error: true, isRetryable: true };
        }

        throw new Error(`status=${response.status}, response=${JSON.stringify(json)}`);
    } catch (error) {
        // Currently, the backend may send empty JSON responses, as the fix has not been deployed yet.
        // No need to notify sentry
        if (`${error}` === 'SyntaxError: Unexpected end of JSON input') {
            return { error: true };
        }
        Sentry.captureMessage(`Unexpected error happened when verifying code - ${error}`);
        // eslint-disable-next-line no-console
        console.error('Error:', error);

        return { error: true };
    }
};

/**
 * Verifies a TOTP code for the new authenticator (Phase 2) sign-in flow. Posts to /sessions/verify.
 *
 * Kept separate from the legacy `verifyCode` so the existing email OTP flow is untouched. Unlike
 * `verifyCode`, this parses the body defensively and branches on HTTP status instead of matching
 * engine-specific JSON parse error strings, and keeps the user on the screen for any failure.
 */
export const verifyAuthenticatorCode = async (data: {
    code: string;
    return_to?: string | null;
}) => {
    const { code, return_to } = data;

    try {
        const response = await fetchWithCsrf(`${backendHost}${backendLoginVerifyUrl}`, {
            method: 'POST',
            mode: backendHost?.length ? 'cors' : 'same-origin',
            headers: apiHeaders,
            body: JSON.stringify({ code, return_to }),
        });
        // Tolerate empty / non-JSON bodies: an invalid code returns 401 with an empty body and a 5xx
        // may return HTML, neither of which should be treated as a fatal parse error.
        const json: VerifyResponse = await Promise.resolve(response.json()).catch(
            () => ({}) as VerifyResponse,
        );
        const { redirect_to, error, user_id, company_id, company_name } = json;

        // Only trust redirect_to on a 2xx: a 4xx/5xx that still carries redirect_to (misconfigured
        // backend/proxy) must not be treated as a successful verification.
        if (response.ok && redirect_to) {
            if (!!user_id && !!company_id && !!company_name) {
                trackFormInteraction({
                    formName: 'signin-otp-form',
                    interactionType: 'submit',
                    account: { companyID: company_id, companyName: company_name },
                    user: { id: user_id },
                });
            } else {
                reportUserDataMissing({ user_id, company_id, company_name });
            }

            return { redirectTo: redirect_to };
        }

        // A 200 with no redirect_to is a malformed success worth flagging; 4xx/5xx (invalid code,
        // rate limited) are expected failures we surface inline. Branching on status avoids relying
        // on engine-specific JSON parse messages.
        if (response.ok) {
            Sentry.captureMessage('No redirect_to when verifying authenticator code');
            return { error: true };
        }

        // The single-use verification session was consumed/expired: the user cannot retry here, so
        // flag it distinctly and let the caller bounce them back to login. Key strictly on the
        // backend's error marker: the attempts-exceeded response also carries a redirect_to (to
        // password reset), and must NOT be misclassified as session-expired.
        if (error === SESSION_EXPIRED_ERROR) {
            return { error: true, sessionExpired: true };
        }

        return { error: true };
    } catch (error) {
        // Network/connection failure.
        Sentry.captureMessage(`Network error happened when verifying authenticator code - ${error}`);
        // eslint-disable-next-line no-console
        console.error('Error:', error);

        return { error: true };
    }
};

/**
 * Logs the user in with a single-use backup code when they can't access their authenticator app.
 * Posts to /sessions/verify_backup (API contract §4).
 *
 * - Success 200: `{ redirect_to, ...analytics, backup_code_used: true, backup_codes_remaining }`.
 * - Invalid code 401: empty body -> treated as a generic error (same as /sessions/verify).
 * - Rate limited 429: `{ error: 'rate_limited', retry_after }` -> generic error.
 */
export const verifyBackupCode = async (data: {
    backup_code: string;
    return_to?: string | null;
}) => {
    const { backup_code, return_to } = data;

    try {
        const response = await fetchWithCsrf(`${backendHost}${backendLoginVerifyBackupUrl}`, {
            method: 'POST',
            mode: backendHost?.length ? 'cors' : 'same-origin',
            headers: apiHeaders,
            body: JSON.stringify({ backup_code, return_to }),
        });
        // Tolerate empty / non-JSON bodies: an invalid code returns 401 with an empty body and a 5xx
        // may return HTML, neither of which should be treated as a fatal parse error.
        const json: VerifyBackupResponse = await Promise.resolve(response.json()).catch(
            () => ({}) as VerifyBackupResponse,
        );
        const { redirect_to, error, user_id, company_id, company_name } = json;

        // Only trust redirect_to on a 2xx: a 4xx/5xx that still carries redirect_to (misconfigured
        // backend/proxy) must not be treated as a successful verification.
        if (response.ok && redirect_to) {
            if (!!user_id && !!company_id && !!company_name) {
                trackFormInteraction({
                    formName: 'signin-backup-code-form',
                    interactionType: 'submit',
                    account: { companyID: company_id, companyName: company_name },
                    user: { id: user_id },
                });
            } else {
                reportUserDataMissing({ user_id, company_id, company_name });
            }

            return { redirectTo: redirect_to };
        }

        // A 200 with no redirect_to is a malformed success worth flagging; 4xx/5xx (invalid code,
        // rate limited) are expected failures we surface inline without Sentry noise. Branching on
        // status avoids relying on engine-specific JSON parse error messages.
        if (response.ok) {
            Sentry.captureMessage('No redirect_to when verifying backup code');
            return { error: true };
        }

        // The single-use verification session was consumed/expired: the user cannot retry here, so
        // flag it distinctly and let the caller bounce them back to login. Key strictly on the
        // backend's error marker: the attempts-exceeded response also carries a redirect_to (to
        // password reset), and must NOT be misclassified as session-expired.
        if (error === SESSION_EXPIRED_ERROR) {
            return { error: true, sessionExpired: true };
        }

        return { error: true };
    } catch (error) {
        // Network/connection failure.
        Sentry.captureMessage(`Network error happened when verifying backup code - ${error}`);
        // eslint-disable-next-line no-console
        console.error('Error:', error);

        return { error: true };
    }
};
